import * as React from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';

import { ExtraLargeTextStyles } from '../design-system/Text';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  align-items: left;
  height: 100%;
  padding: 38px 45px;
  border: 1px solid ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.55)};

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 30px;
  }
`;

const Quote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  line-height: 1.35;
  opacity: 1;
  width: 100%;

  blockquote {
    ${ExtraLargeTextStyles}

    &::before {
      content: '\u201C';
      flex: 1 1 auto;
      display: flex;
      margin-left: -0.95rem;
      height: 1px;
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    blockquote {
      font-size: 22px;
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    blockquote::before {
      margin-left: -0.8rem;
    }
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: start;

  img {
    max-height: 45px;
    object-fit: contain;
    ${({ theme }) => theme.customerLogoColor};
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  min-height: 45px;

  img {
    clip-path: circle();
    filter: none;
    object-fit: cover;
    margin-right: 12px;
    height: 35px;
    width: 35px;
  }

  p {
    color: ${({ theme }) => theme.colors.WHITE};
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    font-weight: 600;
    margin-bottom: 0;
  }

  span {
    color: ${({ theme }) => theme.fontColor.DEFAULT};
    font-weight: 400;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    p:first-child {
      font-size: 14px;
    }
  }
`;

export interface CustomerQuoteCardProps {
  author?: string;
  authorAvatarPath?: string;
  authorTitle: string;
  customerName?: string;
  logoPath?: string | React.ReactNode;
  quote: string;
}

export const CustomerQuoteCard: React.ComponentType<CustomerQuoteCardProps> =
  React.memo(
    ({
      logoPath,
      customerName,
      quote,
      authorAvatarPath,
      author,
      authorTitle
    }) => {
      return (
        <Wrapper>
          <Quote>
            <Logo>
              {typeof logoPath === 'string' ? (
                <img
                  alt={customerName || 'customer logo'}
                  loading="lazy"
                  src={logoPath}
                />
              ) : (
                logoPath
              )}
            </Logo>
            <blockquote>{quote}”</blockquote>
            {author && (
              <Author>
                {authorAvatarPath && (
                  <img alt={author} loading="lazy" src={authorAvatarPath} />
                )}
                <p>
                  {author}
                  <span>
                    &nbsp;&nbsp;·&nbsp;&nbsp;{authorTitle}{' '}
                    {customerName && <>at {customerName}</>}
                  </span>
                </p>
              </Author>
            )}
          </Quote>
        </Wrapper>
      );
    }
  );
